import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";

import createTeamConfig from "../config/createTeamConfig.json";
import { getVueltaRegistrationStatus } from "../config/competitionConfig";
import "./styles/ManageTeamLeaguePage.css";

const K_BLUE = "#0D47A1";
const K_YELLOW = "#FFF70D";

const dateOnly = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const safeParseDateOnly = (raw) => {
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return dateOnly(d);
  } catch {
    return null;
  }
};

const fmtDateByLang = (d, lang) => {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear());

  if ((lang || "fr").startsWith("en")) return `${mm}/${dd}/${yy}`;
  return `${dd}/${mm}/${yy}`;
};

const buildChangeWindows = (config) => {
  const keys = Object.keys(config)
    .filter((k) => k.toLowerCase().startsWith("changeabledate"))
    .sort((a, b) => {
      const getNum = (s) => Number((s.match(/\d+/) || ["0"])[0]);
      return getNum(a) - getNum(b);
    });

  const dates = [];
  for (const k of keys) {
    const d = safeParseDateOnly(config[k]);
    if (d) dates.push(d);
  }

  const windows = [];
  for (let i = 0; i < dates.length; i += 2) {
    const a = dates[i];
    const b = dates[i + 1];
    if (!b) {
      windows.push({ start: a, endInc: a });
      continue;
    }
    const start = a <= b ? a : b;
    const endInc = a <= b ? b : a;
    windows.push({ start, endInc });
  }

  return windows;
};

const getTeamChangeStatus = (windows) => {
  const today = dateOnly(new Date());
  let open = false;
  let nextOpen = null;

  for (const w of windows) {
    const start = dateOnly(w.start);
    const endInc = dateOnly(w.endInc);

    const inRange = today >= start && today <= endInc;
    if (inRange) {
      open = true;
      break;
    }

    if (today < start) {
      if (!nextOpen || start < nextOpen) nextOpen = start;
    }
  }

  return { open, nextOpen };
};

const getBonusChangeStatus = () => {
  const today = dateOnly(new Date());
  const open = today.getDate() === 1;

  const next = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  return { open, nextOpen: next };
};

const CardAction = ({ title, enabled, disabledText, onClick }) => {
  return (
    <button
      type="button"
      className={`mtl-card ${enabled ? "" : "is-disabled"}`}
      onClick={enabled ? onClick : undefined}
    >
      <div className="mtl-card-left">
        <span className="mtl-icon" aria-hidden="true">
          ⓘ
        </span>
        <div className="mtl-card-title">{title}</div>
      </div>

      <div className="mtl-card-right">
        {enabled ? (
          <span className="mtl-arrow" aria-hidden="true">
            →
          </span>
        ) : (
          <span className="mtl-disabled-text">{disabledText}</span>
        )}
      </div>
    </button>
  );
};

const Section = ({ title, children }) => {
  return (
    <div className="mtl-section">
      <div className="mtl-section-header">
        <div className="mtl-section-title">{title}</div>
        <div className="mtl-yellow-bar" />
      </div>
      <div className="mtl-section-body">{children}</div>
    </div>
  );
};

const ManageTeamLeaguePage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const lang = i18n.resolvedLanguage || i18n.language || "fr";

  const closeDate = useMemo(() => safeParseDateOnly(createTeamConfig.closeDate), []);

  const isRegistrationOpen = useMemo(() => {
    if (!closeDate) return true;
    const today = dateOnly(new Date());
    return !(today > closeDate);
  }, [closeDate]);

  const teamWindows = useMemo(() => buildChangeWindows(createTeamConfig), []);

  const { open: teamOpen, nextOpen: nextTeamOpen } = useMemo(
    () => getTeamChangeStatus(teamWindows),
    [teamWindows]
  );

  const { open: bonusOpen, nextOpen: nextBonusOpen } = useMemo(() => getBonusChangeStatus(), []);

  const closedLabel = t("manageTeamLeague.card.closed");

  const nextOpenLabel = (d) =>
    t("manageTeamLeague.card.nextOpen", { date: fmtDateByLang(d, lang) });

  const noNextTransferLabel = t("manageTeamLeague.card.noNextTransfer");
  const vueltaStatus = getVueltaRegistrationStatus();
  const vueltaIsOpen = vueltaStatus === "open";
  const vueltaDisabledText =
    vueltaStatus === "before"
      ? t("manageTeamLeague.vuelta.opensAt")
      : t("manageTeamLeague.vuelta.closed");

  return (
    <div className="mtl-page">
      <div className="mtl-topbar">
        <button className="mtl-back" onClick={() => navigate(-1)} aria-label={t("manageTeamLeague.back")}>
          ←
        </button>
        <div className="mtl-topbar-title">{t("manageTeamLeague.title")}</div>
        <div style={{ width: 34 }} />
      </div>

      <div className="mtl-content">
        {isRegistrationOpen ? (
          <Section title={t("manageTeamLeague.sections.beforeClose")}>
            <CardAction
              title={t("manageTeamLeague.actions.createOrEditWt")}
              enabled={true}
              disabledText={closedLabel}
              onClick={() => navigate("/createTeam")}
            />
            <CardAction
              title={t("manageTeamLeague.actions.createOrEditGiro")}
              enabled={true}
              disabledText={closedLabel}
              onClick={() => navigate("/createGiroTeam")}
            />
            <CardAction
              title={t("manageTeamLeague.actions.createOrEditTdf", {
                defaultValue: "Créer / modifier mon équipe Tour de France",
              })}
              enabled={true}
              disabledText={closedLabel}
              onClick={() => navigate("/createTdfTeam")}
            />
            <CardAction
              title={t("manageTeamLeague.actions.createOrEditVuelta")}
              enabled={vueltaIsOpen}
              disabledText={vueltaDisabledText}
              onClick={() => navigate("/createVueltaTeam")}
            />
            <CardAction
              title={t("manageTeamLeague.actions.createLeague")}
              enabled={true}
              disabledText={closedLabel}
              onClick={() => navigate("/leagues/create")}
            />
            <CardAction
              title={t("manageTeamLeague.actions.joinLeague")}
              enabled={true}
              disabledText={closedLabel}
              onClick={() => navigate("/leagues/join")}
            />
          </Section>
        ) : (
          <Section title={t("manageTeamLeague.sections.afterClose")}>
            <CardAction
              title={t("manageTeamLeague.actions.changeWtTeam")}
              enabled={teamOpen}
              disabledText={
                nextTeamOpen ? nextOpenLabel(nextTeamOpen) : noNextTransferLabel
              }
              onClick={() => navigate("/changeTeam")}
            />
            <CardAction
              title={t("manageTeamLeague.actions.createOrEditGiro")}
              enabled={true}
              disabledText={closedLabel}
              onClick={() => navigate("/createGiroTeam")}
            />
            <CardAction
              title={t("manageTeamLeague.actions.createOrEditTdf", {
                defaultValue: "Créer / modifier mon équipe Tour de France",
              })}
              enabled={true}
              disabledText={closedLabel}
              onClick={() => navigate("/createTdfTeam")}
            />
            <CardAction
              title={t("manageTeamLeague.actions.createOrEditVuelta")}
              enabled={vueltaIsOpen}
              disabledText={vueltaDisabledText}
              onClick={() => navigate("/createVueltaTeam")}
            />
            <CardAction
              title={t("manageTeamLeague.actions.changeWtBonus")}
              enabled={bonusOpen}
              disabledText={nextOpenLabel(nextBonusOpen)}
              onClick={() => navigate("/changeBonusCyclist")}
            />
            <CardAction
              title={t("manageTeamLeague.actions.changeLeagueTeam")}
              enabled={teamOpen}
              disabledText={
                nextTeamOpen ? nextOpenLabel(nextTeamOpen) : noNextTransferLabel
              }
              onClick={() => navigate("/leagues/team/change")}
            />
            <CardAction
              title={t("manageTeamLeague.actions.changeLeagueBonus")}
              enabled={bonusOpen}
              disabledText={nextOpenLabel(nextBonusOpen)}
              onClick={() => navigate("/leagues/bonus/change")}
            />
          </Section>
        )}
      </div>

      <style>{`
        :root {
          --mtl-blue: ${K_BLUE};
          --mtl-yellow: ${K_YELLOW};
        }
      `}</style>
    </div>
  );
};

export default ManageTeamLeaguePage;
